import React, { useRef } from "react";
import { CJDropshippingApp } from "@elakay/cj-dropshipping";
import { PrintfulApp } from "@elakay/printful-dropshipping";
import { SynceeApp } from "@elakay/syncee-dropshipping";
import { PrintifyApp } from "@elakay/printify-dropshipping";
import { InventorySourceApp } from "@elakay/inventory-source-dropshipping";
import { Navigate, useLocation } from "react-router-dom";
import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";
import { elakayAutoText } from "../pageInside/dataElakay";
import Layout from "../Layout/Layout";
import { getDropshippingOutPlatforms, normalizeDropshippingPlatformMode } from "./dropshippingOutPlatforms";
import { canViewDropshippingSellerCenter, canViewDropshippingPlatform, getCurrentElakayRole } from '../pageInside/Rolle';

const CJDropshippingDashboard = ({ infoSource }) => {
  const contentTopRef = useRef(null);
  const session = isAuthenticated();
  const effectiveRoleName = getCurrentElakayRole(infoSource) || "";
  const { pathname } = useLocation();
  const isPrintful = pathname.startsWith("/dropshipping/printful");
  const isSyncee = pathname.startsWith("/dropshipping/syncee");
  const isPrintify = pathname.startsWith("/dropshipping/printify");
  const isInventorySource = pathname.startsWith("/dropshipping/inventory-source");
  const platformId = isInventorySource
    ? "inventory-source"
    : isPrintify
      ? "printify"
      : isSyncee
        ? "syncee"
        : isPrintful
          ? "printful"
          : "cj-dropshipping";
  const platformMode = normalizeDropshippingPlatformMode(
    infoSource?.allSettings?.dropshippingOut?.platformModes?.[platformId]
  );
  const visiblePlatformIds = getDropshippingOutPlatforms(infoSource?.allSettings?.dropshippingOut)
    .filter((platform) => canViewDropshippingPlatform(infoSource, platform.id))
    .map((platform) => platform.id);
  if (!canViewDropshippingSellerCenter(infoSource) || !canViewDropshippingPlatform(infoSource, platformId)) {
    return <Navigate to="/myshop" replace />;
  }
  return (
    <Layout
      infoSource={infoSource}
      titleElakay="dropshipping"
      SliderPunShow={false}
      loading={false}
      outOffPage={false}
      {...infoSource?.allSettings?.layout}
      showFilterMenu={true}
      showFooter={true}
    >
      <style>{`
        .cjds-shell, .pfds-shell, .synds-shell, .pyds-shell, .isds-shell { min-height: 60vh !important; }
        .dropshipping-environment-banner { margin: 12px auto; width: min(1180px, calc(100% - 32px)); padding: 10px 14px; border-radius: 10px; font-weight: 800; text-align: center; box-sizing: border-box; }
        .dropshipping-environment-banner--test { color: #8a5a00; background: #fff3cd; border: 1px solid #f0cf6b; }
        .dropshipping-environment-banner--live { color: #157347; background: #d8f3e5; border: 1px solid #8fd1ae; }
      `}</style>
      {/*
        Ancre placée juste sous le Header (premier élément du contenu rendu
        par Layout, qui affiche déjà Header+Sidebar avant `children`). Le
        Header est `position:absolute` sur desktop mais devient
        `position:fixed` en dessous de 900px (Header.css:757-786, hauteur
        constante 85px) -- `scrollMarginTop` compense ce cas pour que
        `scrollIntoView` n'amène jamais ce point sous le Header fixe.
        Utilisé par ProductCollectionPage.js (pagination Previous/Next) via
        CJDropshippingContext pour revenir ici en scroll fluide, sans
        remonter tout en haut du navigateur.
      */}
      <div ref={contentTopRef} style={{ scrollMarginTop: 96 }} />
      <div
        className={`dropshipping-environment-banner dropshipping-environment-banner--${platformMode}`}
        role="status"
      >
        {platformMode === "test"
          ? elakayAutoText("h_dropshipping_mode_test_banner", "TEST mode — verification environment")
          : elakayAutoText("h_dropshipping_mode_live_banner", "LIVE mode — production environment")}
      </div>
      {isInventorySource ? (
        <InventorySourceApp
          baseUrl={elakay_API}
          token={session?.token || ""}
          roleName={effectiveRoleName}
          translate={elakayAutoText}
          environmentMode={platformMode}
        />
      ) : isPrintify ? (
        <PrintifyApp
          baseUrl={elakay_API}
          token={session?.token || ""}
          roleName={effectiveRoleName}
          translate={elakayAutoText}
          environmentMode={platformMode}
        />
      ) : isSyncee ? (
        <SynceeApp
          baseUrl={elakay_API}
          token={session?.token || ""}
          roleName={effectiveRoleName}
          translate={elakayAutoText}
          environmentMode={platformMode}
        />
      ) : isPrintful ? (
        <PrintfulApp
          baseUrl={elakay_API}
          token={session?.token || ""}
          roleName={effectiveRoleName}
          translate={elakayAutoText}
          environmentMode={platformMode}
        />
      ) : (
        <CJDropshippingApp
          baseUrl={elakay_API}
          token={session?.token || ""}
          userId={infoSource?.userInfo?._id || ""}
          roleName={effectiveRoleName}
          translate={elakayAutoText}
          environmentMode={platformMode}
          visiblePlatformIds={visiblePlatformIds}
          contentTopRef={contentTopRef}
        />
      )}
    </Layout>
  );
};

export default CJDropshippingDashboard;
